import * as styles from "../styles";
import {
  SEARCHABLE_LIST_FLEX,
  SEARCHABLE_LIST_MIN_HEIGHT_PX,
  SEARCHABLE_LIST_PAGE_SIZE,
  SEARCHABLE_LIST_PAGINATION_THRESHOLD,
  SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
} from "../types/constants";
import ClearableSearchField from "./ClearableSearchField";
import TruncatedText from "./TruncatedText";
import {
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { Box, IconButton, MenuItem, MenuList, Typography } from "@mui/material";
import Button from "components/common/Button";
import * as labels from "config.labels";
import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const SearchableList = ({
  items,
  onItemClick,
  isOpen,
  searchPlaceholder,
  header,
  loading = false,
  emptyMessage,
  sortAlphabetically = true,
  actionBar,
  searchValue: externalSearchValue,
  onSearchChange: externalOnSearchChange,
  onReorder,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [draggedId, setDraggedId] = useState(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const isControlled =
    externalSearchValue !== undefined && externalOnSearchChange !== undefined;
  const searchValue = isControlled ? externalSearchValue : internalSearchValue;
  const setSearchValue = isControlled
    ? externalOnSearchChange
    : setInternalSearchValue;

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      const target = e.target;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }
      // Space on a focused value or button picks it, as it does elsewhere,
      // rather than adding a space to the search.
      if (e.key === " " && target.closest?.('[role="menuitem"], button')) {
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setSearchValue(searchValue + e.key);
      } else if (e.key === "Backspace" && searchValue.length > 0) {
        e.preventDefault();
        setSearchValue(searchValue.slice(0, -1));
      } else if (e.key === "Delete" && searchValue.length > 0) {
        e.preventDefault();
        setSearchValue("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, searchValue, setSearchValue]);

  useEffect(() => {
    if (!isOpen && !isControlled && internalSearchValue) {
      setInternalSearchValue("");
    }
  }, [isOpen, isControlled, internalSearchValue]);

  const filteredItems = useMemo(() => {
    let result = items;

    if (searchValue) {
      const query = searchValue.toLowerCase();
      result = result.filter((item) =>
        item.label.toLowerCase().includes(query)
      );
    }

    if (sortAlphabetically) {
      result = [...result].sort((a, b) => a.label.localeCompare(b.label));
    }
    return result;
  }, [items, searchValue, sortAlphabetically]);

  const [currentPage, setCurrentPage] = useState(0);

  const needsPagination =
    filteredItems.length > SEARCHABLE_LIST_PAGINATION_THRESHOLD;
  const totalPages = needsPagination
    ? Math.ceil(filteredItems.length / SEARCHABLE_LIST_PAGE_SIZE)
    : 1;

  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setCurrentPage(0);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue]);

  const pageItems = useMemo(() => {
    if (!needsPagination) return filteredItems;
    const start = currentPage * SEARCHABLE_LIST_PAGE_SIZE;
    return filteredItems.slice(start, start + SEARCHABLE_LIST_PAGE_SIZE);
  }, [filteredItems, needsPagination, currentPage]);

  useEffect(() => {
    if (needsPagination && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [currentPage, needsPagination]);

  const totalCount = useMemo(() => {
    const sum = items.reduce((acc, item) => acc + (item.count ?? 0), 0);
    return sum > 0 ? sum : 1;
  }, [items]);
  // Drag-to-reorder is only meaningful when the list shows the real order —
  // disable it while searching or paginating (filtered subset isn't the order).
  const reorderable = Boolean(onReorder) && !searchValue && !needsPagination;
  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {header && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <IconButton
            size={"small"}
            onClick={header.onBack}
            aria-label={translateLabels(labels.SEARCHABLE_LIST_BACK, {
              default: "Back",
            })}
          >
            <ArrowBackIcon fontSize={"small"} />
          </IconButton>
          <Typography
            variant={"subtitle2"}
            sx={{
              ml: 1,
              flexGrow: 1,
            }}
          >
            {header.title}
          </Typography>
          {header.endContent}
        </Box>
      )}
      {/* Search Field - this element defines the container width */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <ClearableSearchField
          value={searchValue}
          onChange={setSearchValue}
          placeholder={
            searchPlaceholder ??
            translateLabels(labels.SEARCHABLE_LIST_SEARCH_PLACEHOLDER, {
              default: "search",
            })
          }
          countDisplay={
            loading
              ? undefined
              : {
                  filtered: filteredItems.length,
                  total: items.length,
                }
          }
        />
      </Box>
      {actionBar && <Box sx={{ px: 1.5, pb: 1 }}>{actionBar}</Box>}
      {needsPagination && currentPage > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 0.5,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button size={"small"} onClick={() => setCurrentPage((p) => p - 1)}>
            {translateLabels(labels.SEARCHABLE_LIST_PREV_PAGE, {
              default: "Prev ({page}/{total})",
              replacements: { page: currentPage, total: totalPages },
            })}
          </Button>
        </Box>
      )}
      {/* Items List - positioned absolute so it doesn't affect parent width */}
      <Box
        sx={{
          flex: SEARCHABLE_LIST_FLEX,
          minHeight: SEARCHABLE_LIST_MIN_HEIGHT_PX,
          position: "relative",
        }}
      >
        <MenuList
          ref={listRef}
          dense
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {loading ? (
            <MenuItem disabled>
              <Typography variant={"body2"} color={"text.secondary"}>
                {translateLabels(labels.SEARCHABLE_LIST_LOADING, {
                  default: "Loading...",
                })}
              </Typography>
            </MenuItem>
          ) : filteredItems.length === 0 ? (
            <MenuItem disabled>
              <Typography variant={"body2"} color={"text.secondary"}>
                {emptyMessage ??
                  translateLabels(labels.SEARCHABLE_LIST_EMPTY_MESSAGE, {
                    default: "No items found",
                  })}
              </Typography>
            </MenuItem>
          ) : (
            pageItems.map((item) => {
              const itemCount = item.count ?? 0;
              const countPercentage =
                itemCount > 0 ? (itemCount / totalCount) * 100 : 0;
              return (
                <MenuItem
                  key={item.id}
                  onClick={(e) => onItemClick(item, e)}
                  selected={item.selected}
                  draggable={reorderable}
                  onDragStart={
                    reorderable
                      ? (e) => {
                          setDraggedId(item.id);
                          e.dataTransfer.effectAllowed = "move";
                        }
                      : undefined
                  }
                  onDragOver={
                    reorderable ? (e) => e.preventDefault() : undefined
                  }
                  onDrop={
                    reorderable
                      ? (e) => {
                          e.preventDefault();
                          if (draggedId && draggedId !== item.id) {
                            onReorder(draggedId, item.id);
                          }
                          setDraggedId(null);
                        }
                      : undefined
                  }
                  onDragEnd={reorderable ? () => setDraggedId(null) : undefined}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    cursor: reorderable ? "grab" : undefined,
                    opacity: reorderable && draggedId === item.id ? 0.4 : 1,
                    backgroundColor: item.selected
                      ? "action.selected"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: item.selected
                        ? "action.focus"
                        : "action.hover",
                    },
                    ...(item.status && {
                      borderLeft: "3px solid",
                      borderLeftColor:
                        item.status === "added" ? "success.main" : "error.main",
                    }),
                    ...item.sx,
                  }}
                >
                  {reorderable && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        color: "text.disabled",
                        position: "relative",
                        zIndex: SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
                        ml: -0.5,
                      }}
                    >
                      <DragIndicatorIcon fontSize={"small"} />
                    </Box>
                  )}
                  {itemCount > 0 && (
                    <Box
                      sx={{ ...styles.countBar, width: `${countPercentage}%` }}
                    />
                  )}
                  {item.startIcon && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
                      }}
                    >
                      {item.startIcon}
                    </Box>
                  )}
                  <TruncatedText
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: "relative",
                      zIndex: SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
                      fontSize: "0.875rem",
                      fontWeight: item.selected ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </TruncatedText>
                  {item.endIcon && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
                      }}
                    >
                      {item.endIcon}
                    </Box>
                  )}
                  {item.endAdornment && (
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
                      }}
                    >
                      {item.endAdornment}
                    </Box>
                  )}
                  {itemCount > 0 && (
                    <Typography
                      variant={"body2"}
                      sx={{
                        color: "text.secondary",
                        flexShrink: 0,
                        position: "relative",
                        zIndex: SEARCHABLE_LIST_ROW_CONTENT_Z_INDEX,
                      }}
                    >
                      {itemCount.toLocaleString()}
                    </Typography>
                  )}
                </MenuItem>
              );
            })
          )}
        </MenuList>
      </Box>
      {needsPagination && currentPage < totalPages - 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 0.5,
            borderTop: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button size={"small"} onClick={() => setCurrentPage((p) => p + 1)}>
            {translateLabels(labels.SEARCHABLE_LIST_NEXT_PAGE, {
              default: "Next ({page}/{total})",
              replacements: { page: currentPage + 2, total: totalPages },
            })}
          </Button>
        </Box>
      )}
    </Box>
  );
};
export default SearchableList;
